/**
 * ข้อความความล้มเหลวของหน้าจอ SSO admin — หนึ่งกฎ หนึ่งข้อความ
 *
 * กฎที่รู้จักมาจาก `admin_service.py` (D-175) เท่านั้น; กฎอื่นตกไปที่ข้อความรวมของ
 * สถานะ HTTP พร้อมแสดงรหัสกฎและข้อความจากเซิร์ฟเวอร์ตามจริง
 */

import { StatusBadge } from '../StatusBadge';
import { ruleFromFailure } from '../../lib/rbacAdmin/client';
import type { SsoAdminFailure } from '../../lib/ssoAdmin/client';
import { SSO_RULES } from '../../lib/ssoAdmin/types';

interface Copy {
  readonly heading: string;
  readonly text: string;
}

function ruleCopy(rule: string | null): Copy | null {
  switch (rule) {
    case SSO_RULES.REDIRECT_URI_DOMAIN_NOT_ALLOWED:
      return {
        heading: 'โดเมนของ redirect URI ไม่อยู่ในรายการที่อนุญาต',
        text: 'redirect URI แบบ HTTPS ต้องอยู่ในโดเมนที่ผู้ดูแลระบบตั้งไว้ในการตั้งค่าของเซิร์ฟเวอร์เท่านั้น ตรวจชื่อโดเมนอีกครั้ง หรือขอให้ผู้ดูแลระบบเพิ่มโดเมนนี้ก่อน ไม่มีการบันทึกใด ๆ',
      };
    case SSO_RULES.REDIRECT_URI_INVALID:
      return {
        heading: 'redirect URI รูปแบบไม่ถูกต้อง',
        text: 'URI ต้องเป็นที่อยู่เต็มที่มีชื่อ host และไม่มี fragment (#) ตรวจทุกบรรทัดในช่อง URI',
      };
    case SSO_RULES.REDIRECT_URI_SCHEME:
      return {
        heading: 'redirect URI ต้องเป็น HTTPS',
        text: 'อนุญาตเฉพาะ https:// (หรือ http://localhost เมื่อเปิด "อนุญาต localhost แบบไม่เข้ารหัส") scheme อื่นถูกปฏิเสธ',
      };
    case SSO_RULES.REDIRECT_URI_UNSAFE_CHARACTER:
      return {
        heading: 'redirect URI มีอักขระที่ไม่ปลอดภัย',
        text: 'URI ต้องเป็น ASCII ล้วน ห้ามมีช่องว่าง อักขระควบคุม หรือเครื่องหมาย \\ ตรวจทุกบรรทัด',
      };
    case SSO_RULES.REDIRECT_URI_LOOPBACK_NOT_ALLOWED:
      return {
        heading: 'ไม่อนุญาต localhost สำหรับ client นี้',
        text: 'URI แบบ http://localhost ใช้ได้เมื่อติ๊ก "อนุญาต localhost แบบไม่เข้ารหัส" ของ client เท่านั้น (ใช้เฉพาะการพัฒนา)',
      };
    case SSO_RULES.REDIRECT_URI_KIND_OVERLAP:
      return {
        heading: 'URI เดียวใช้เป็นทั้ง callback และ post-logout ไม่ได้',
        text: 'แยก URI ของการล็อกอินกลับ (redirect) กับหน้าที่ไปหลังออกจากระบบ (post-logout) เป็นคนละที่อยู่',
      };
    case SSO_RULES.APP_ROLE_NOT_FOUND:
      return {
        heading: 'ไม่พบ role นี้ในแอป',
        text: 'role ที่เลือก (หรือ MFA role) ต้องเป็น role ที่ยังใช้งานของแอปนั้น รายการอาจเปลี่ยนไประหว่างที่หน้าจอเปิดค้าง โหลดใหม่แล้วเลือกอีกครั้ง',
      };
    case SSO_RULES.APP_NOT_FOUND:
      return {
        heading: 'ไม่พบแอปนี้',
        text: 'แอปที่เลือกไม่มีอยู่หรือถูกปิดแล้ว โหลดรายการใหม่แล้วเลือกอีกครั้ง',
      };
    case SSO_RULES.APP_CODE_TAKEN:
      return {
        heading: 'รหัสแอปนี้ถูกลงทะเบียนแล้ว',
        text: 'รหัสแอปต้องไม่ซ้ำ ใช้รหัสอื่น หรือเพิ่ม role ให้แอปเดิมแทน',
      };
    case SSO_RULES.APP_ROLE_TAKEN:
    case SSO_RULES.APP_ROLE_DUPLICATE:
      return {
        heading: 'รหัส role ซ้ำในแอปนี้',
        text: 'แต่ละ role ของแอปมีรหัสได้ครั้งเดียว ใช้รหัสอื่น',
      };
    case SSO_RULES.CLIENT_ID_TAKEN:
      return {
        heading: 'client_id นี้ถูกใช้แล้ว',
        text: 'client_id ต้องไม่ซ้ำ ใช้รหัสอื่น ไม่มี client ใดถูกสร้างและไม่มี secret ถูกออก',
      };
    case SSO_RULES.CLIENT_NOT_FOUND:
      return {
        heading: 'ไม่พบ client นี้',
        text: 'client อาจถูกเปลี่ยนโดยผู้ดูแลอีกคน โหลดรายการใหม่',
      };
    case SSO_RULES.EMPLOYEE_NOT_FOUND:
      return {
        heading: 'ไม่พบพนักงานรหัสนี้',
        text: 'รหัสพนักงานเป็นรหัสที่องค์กรกำหนดเอง ตรวจว่าพิมพ์ถูกต้อง ไม่มีสิทธิ์ใดถูกเปลี่ยน',
      };
    case SSO_RULES.APP_ROLE_NOT_HELD:
      return {
        heading: 'พนักงานคนนี้ไม่ได้ถือสิทธิ์ในแอปนี้อยู่แล้ว',
        text: 'อาจมีผู้ดูแลอีกคนถอนไปก่อน โหลดรายการใหม่เพื่อดูสถานะจริง',
      };
    case SSO_RULES.CONCURRENT_CHANGE:
      return {
        heading: 'มีการเปลี่ยนแปลงชนกัน',
        text: 'มีผู้ดูแลอีกคนแก้ข้อมูลเดียวกันพร้อมกัน ไม่มีสิ่งใดถูกบันทึกจากคำขอนี้ โหลดข้อมูลใหม่แล้วทำอีกครั้ง',
      };
    default:
      return null;
  }
}

function genericCopy(failure: SsoAdminFailure): Copy {
  switch (failure.status) {
    case 'unauthenticated':
      return {
        heading: 'ต้องเข้าสู่ระบบก่อน',
        text: 'เซสชันหมดอายุหรือถูกยกเลิก (401) เข้าสู่ระบบใหม่',
      };
    case 'forbidden':
      return {
        heading: 'บัญชีนี้ไม่ใช่ผู้ดูแลระบบ',
        text: 'เซิร์ฟเวอร์ตอบ 403 — หน้าจอนี้ต้องใช้สิทธิ์ RBAC_ADMIN ซึ่งมีเฉพาะบทบาท ADMIN',
      };
    case 'not-found':
      return { heading: 'ไม่พบสิ่งที่อ้างถึง', text: 'เซิร์ฟเวอร์ตอบ 404 โหลดรายการใหม่แล้วลองอีกครั้ง' };
    case 'conflict':
      return { heading: 'ข้อมูลชนกับที่มีอยู่', text: 'เซิร์ฟเวอร์ตอบ 409 โหลดรายการใหม่แล้วลองอีกครั้ง' };
    case 'rule-violation':
      return { heading: 'คำขอติดกฎทางธุรกิจ', text: 'เซิร์ฟเวอร์ตอบ 422 แก้เงื่อนไขที่ระบุแล้วลองอีกครั้ง' };
    case 'bad-request':
      return {
        heading: 'คำขอไม่ถูกต้อง',
        text: 'เซิร์ฟเวอร์ตอบ 400 — ข้อมูลที่ส่งไม่ตรงรูปแบบ (เช่น รหัสผิดรูป หรือเว้นช่องบังคับ) ตรวจข้อมูลแล้วลองอีกครั้ง',
      };
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text: 'คำขอไปถึงเซิร์ฟเวอร์แต่ทำงานไม่สำเร็จ โหลดข้อมูลใหม่เพื่อดูสถานะจริงก่อนลองอีกครั้ง',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text: `${failure.detail} — ไม่มีอะไรยืนยันว่าคำขอไปถึงเซิร์ฟเวอร์ โหลดข้อมูลใหม่เพื่อดูสถานะจริงก่อนลองอีกครั้ง`,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดงข้อมูลที่อ่านไม่ครบ ไม่เดาค่าแทน',
      };
    case 'cancelled':
      return { heading: 'ยกเลิกการเรียกข้อมูลแล้ว', text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ' };
  }
}

export interface SsoAdminFailureNoticeProps {
  readonly failure: SsoAdminFailure;
  readonly onRetry?: () => void;
}

export function SsoAdminFailureNotice({ failure, onRetry }: SsoAdminFailureNoticeProps) {
  const rule = ruleFromFailure(failure);
  const copy = ruleCopy(rule) ?? genericCopy(failure);
  const message = 'error' in failure ? failure.error.message : null;
  const refusedByRule = failure.status === 'rule-violation' || failure.status === 'conflict';

  return (
    <div
      className="cap-notice"
      data-testid="sso-admin-failure"
      data-failure={failure.status}
      data-rule={rule ?? ''}
      role="status"
    >
      <StatusBadge tone={refusedByRule ? 'warning' : 'danger'}>
        {refusedByRule ? 'ติดกฎทางธุรกิจ' : 'ทำรายการไม่สำเร็จ'}
      </StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>
      {rule === null ? null : (
        <p className="cap-notice__text">
          กฎที่ชน: <code className="cap-table__code">{rule}</code>
        </p>
      )}
      {message === null ? null : (
        <p className="cap-notice__note">ข้อความจากเซิร์ฟเวอร์: {message}</p>
      )}
      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}
